import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faCalendarCheck,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import * as jobActions from "../../redux/actions/jobActions";
import * as profileActions from "../../redux/actions/profileActions";
import "./dashboard.css";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatCurrency(amount) {
  return `£${Number(amount || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getStatusLabel(status) {
  switch (status) {
    case "installed":  return "Installed";
    case "complete":   return "Complete";
    case "qa-pending": return "QA Pending";
    case "checked":    return "Checked";
    default:           return status;
  }
}

function buildNext14Days(availabilityArr) {
  const available = new Set(
    (availabilityArr || []).map((a) => new Date(a.dateAvailable).toDateString())
  );
  const days = [];
  for (let i = 0; i < 14; i++) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + i);
    days.push({
      date,
      dayLabel: DAY_LABELS[date.getDay()],
      dayNum: date.getDate(),
      available: available.has(date.toDateString()),
    });
  }
  return days;
}

export function Dashboard({ auth, jobs, loadJobs, myJobs, myCompletedJobs, loadMyJobs, loadMyCompletedJobs, myInstalledJobs, loadMyInstalledJobs, myAwaitingPaymentJobs, loadMyAwaitingPaymentJobs, profile, loadProfile }) {
  useEffect(() => {
    if (jobs.length === 0) loadJobs(auth);
    if (myJobs.length === 0) loadMyJobs(auth);
    if (myCompletedJobs.length === 0) loadMyCompletedJobs(auth);
    if (myInstalledJobs.length === 0) loadMyInstalledJobs(auth);
    if (myAwaitingPaymentJobs.length === 0) loadMyAwaitingPaymentJobs(auth);
    if (profile && Object.keys(profile).length === 0) loadProfile(auth);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingJobs = myJobs
    .filter((job) => new Date(job.installDate) >= today)
    .sort((a, b) => new Date(a.installDate) - new Date(b.installDate));

  const completedTotal = myCompletedJobs.reduce((sum, job) => sum + Number(job.engineerTotal || 0), 0);

  const recentlyCompleted = [...myCompletedJobs]
    .sort(
      (a, b) =>
        new Date(b.completionDetail?.installationCompletionDate) -
        new Date(a.completionDetail?.installationCompletionDate)
    )
    .slice(0, 5);

  const awaitingPayment = myAwaitingPaymentJobs;

  const next14Days = buildNext14Days(profile?.availability);
  const availableCount = next14Days.filter((d) => d.available).length;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <p>Here&apos;s a summary of your work with BoxHeat</p>
      </div>

      {/* Stat tiles */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-sm-6 col-md-3">
          <Link to="/jobs" className="tile-link">
            <div className="stat-tile tile-red">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="tile-icon" />
              <div className="tile-value">{jobs.length}</div>
              <div className="tile-label">New Jobs in Your Area</div>
            </div>
          </Link>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <Link to="/jobs?tab=myAssigned" className="tile-link">
            <div className="stat-tile tile-blue">
              <FontAwesomeIcon icon={faCalendarAlt} className="tile-icon" />
              <div className="tile-value">{myJobs.length}</div>
              <div className="tile-label">Your Upcoming &amp; Active Jobs</div>
            </div>
          </Link>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <Link to="/jobs?tab=viewCompleted" className="tile-link">
            <div className="stat-tile tile-green">
              <FontAwesomeIcon icon={faCheckCircle} className="tile-icon" />
              <div className="tile-value">{myInstalledJobs.length}</div>
              <div className="tile-label">Installed &amp; Pending QA Jobs</div>
            </div>
          </Link>
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <Link to="/jobs?tab=viewCompleted" className="tile-link">
            <div className="stat-tile tile-orange">
              <FontAwesomeIcon icon={faClock} className="tile-icon" />
              <div className="tile-value">{awaitingPayment.length}</div>
              <div className="tile-label">Awaiting Payment</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Job lists */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="card-header-custom">
              <h5>Upcoming &amp; Active Jobs</h5>
              <Link to="/jobs?tab=myAssigned" className="card-link">View all</Link>
            </div>
            {upcomingJobs.length === 0 ? (
              <p className="no-data">No upcoming jobs</p>
            ) : (
              <div className="job-list">
                {upcomingJobs.slice(0, 5).map((job) => (
                  <div key={job.orderNumber} className="job-row">
                    <div className="job-info">
                      <span className="job-ref">#{job.orderNumber}</span>
                      <span className="job-postcode">{job.address?.postcode}</span>
                    </div>
                    <div className="job-meta">
                      <span className="job-date">{formatDate(job.installDate)}</span>
                      <span className="job-amount">{formatCurrency(job.engineerTotal)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="card-header-custom">
              <h5>Awaiting Payment</h5>
              <Link to="/jobs?tab=viewCompleted" className="card-link">View all</Link>
            </div>
            {awaitingPayment.length === 0 ? (
              <p className="no-data">No jobs awaiting payment</p>
            ) : (
              <div className="job-list">
                {awaitingPayment.map((job) => (
                  <div key={job.orderNumber} className="job-row row-payment">
                    <div className="job-info">
                      <span className="job-ref">#{job.orderNumber}</span>
                      <span className="job-postcode">{job.address?.postcode}</span>
                    </div>
                    <div className="job-meta">
                      <span className="job-date">
                        {formatDate(job.completionDetail?.qaCheckDate)}
                      </span>
                      <span className="job-amount">{formatCurrency(job.engineerTotal)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recently completed */}
      <div className="row g-3">
        <div className="col-12">
          <div className="dashboard-card">
            <div className="card-header-custom">
              <h5>Recently Completed</h5>
              <Link to="/jobs?tab=viewCompleted" className="card-link">View all</Link>
            </div>
            {recentlyCompleted.length === 0 ? (
              <p className="no-data">No completed jobs yet</p>
            ) : (
              <div className="job-list">
                {recentlyCompleted.map((job) => (
                  <div key={job.orderNumber} className="job-row row-completed">
                    <div className="job-info">
                      <span className="job-ref">#{job.orderNumber}</span>
                      <span className={`status-badge status-${job.orderStatus}`}>
                        {getStatusLabel(job.orderStatus)}
                      </span>
                    </div>
                    <div className="job-meta">
                      <span className="job-date">
                        {formatDate(job.completionDetail?.installationCompletionDate)}
                      </span>
                      <span className="job-amount">{formatCurrency(job.engineerTotal)}</span>
                    </div>
                  </div>
                ))}
                <div className="job-row" style={{ borderTop: "2px solid rgba(255,255,255,0.2)", marginTop: "4px", paddingTop: "8px", color: "#ffffff" }}>
                  <span className="fw-semibold">Value of Completed Orders</span>
                  <span className="fw-semibold" style={{ color: "#10b981" }}>{formatCurrency(completedTotal)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Availability tile */}
      <div className="row g-3 mt-0">
        <div className="col-12">
          <Link to="/engineer?tab=availability" className="tile-link">
            <div className="stat-tile tile-teal availability-tile">
              <div className="availability-header">
                <div>
                  <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                  <span className="tile-label-inline">Availability — Next 2 Weeks</span>
                </div>
                <span className="availability-count">
                  {availableCount} of 14 days available
                </span>
              </div>
              <div className="availability-strip">
                {next14Days.map((day, i) => (
                  <div
                    key={i}
                    className={`day-bubble ${day.available ? "day-available" : "day-unavailable"}`}
                  >
                    <span className="day-name">{day.dayLabel}</span>
                    <span className="day-num">{day.dayNum}</span>
                  </div>
                ))}
              </div>
              <span className="availability-link">Update availability →</span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

Dashboard.propTypes = {
  auth: PropTypes.object.isRequired,
  jobs: PropTypes.array.isRequired,
  loadJobs: PropTypes.func.isRequired,
  myJobs: PropTypes.array.isRequired,
  myCompletedJobs: PropTypes.array.isRequired,
  loadMyJobs: PropTypes.func.isRequired,
  loadMyCompletedJobs: PropTypes.func.isRequired,
  myInstalledJobs: PropTypes.array.isRequired,
  loadMyInstalledJobs: PropTypes.func.isRequired,
  myAwaitingPaymentJobs: PropTypes.array.isRequired,
  loadMyAwaitingPaymentJobs: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  loadProfile: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    jobs: state.jobs,
    myJobs: state.myJobs,
    myCompletedJobs: state.myCompletedJobs,
    myInstalledJobs: state.myInstalledJobs,
    myAwaitingPaymentJobs: state.myAwaitingPaymentJobs,
    profile: state.profile,
  };
}

const mapDispatchToProps = {
  loadJobs: jobActions.loadJobs,
  loadMyJobs: jobActions.loadMyJobs,
  loadMyCompletedJobs: jobActions.loadMyCompletedJobs,
  loadMyInstalledJobs: jobActions.loadMyInstalledJobs,
  loadMyAwaitingPaymentJobs: jobActions.loadMyAwaitingPaymentJobs,
  loadProfile: profileActions.loadProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
